import {useCallback, useEffect, useState} from 'react';

import type {BusinessFeeNegotiation} from '@afriex/core/domain/types';
import {BusinessFeeNegotiationFilter} from '@afriex/core/domain/types';

import {merchantService} from '@/services/merchants';
import {getResponseMessage} from '@/utils/errorMessage';

export const NEGOTIATION_PAGE_SIZE = 20;

type NegotiationCounts = Record<BusinessFeeNegotiationFilter, number>;

const EMPTY_COUNTS: NegotiationCounts = {
  [BusinessFeeNegotiationFilter.Ongoing]: 0,
  [BusinessFeeNegotiationFilter.Completed]: 0,
  [BusinessFeeNegotiationFilter.AwaitingAdmin]: 0,
  [BusinessFeeNegotiationFilter.AwaitingBusiness]: 0,
};

type NegotiationQueueFilters = {
  status: BusinessFeeNegotiationFilter;
  merchantId: string;
};

/**
 * One page of the admin negotiation queue for a tab and an optional merchant
 * search. A response that arrives after the filters moved on is dropped.
 */
export const useFeeNegotiations = ({
  status,
  merchantId,
}: NegotiationQueueFilters) => {
  const [negotiations, setNegotiations] = useState<BusinessFeeNegotiation[]>(
    [],
  );
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<NegotiationCounts>(EMPTY_COUNTS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  // The page belongs to the filters it was picked under, so changing a filter
  // lands on page 1 without an effect to reset it.
  const filterKey = `${status}|${merchantId}`;
  const [pageState, setPageState] = useState({filterKey, page: 1});
  const page = pageState.filterKey === filterKey ? pageState.page : 1;
  const setPage = useCallback(
    (next: number) => setPageState({filterKey, page: next}),
    [filterKey],
  );

  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the loading flag belongs to the fetch this effect starts
    setIsLoading(true);
    setError(null);

    merchantService
      .getFeeNegotiations(
        {
          status,
          merchantId: merchantId || undefined,
          page,
          limit: NEGOTIATION_PAGE_SIZE,
        },
        controller.signal,
      )
      .then((list) => {
        if (controller.signal.aborted) {
          return;
        }
        setNegotiations(list.data);
        setTotal(list.total);
        setCounts(list.counts ?? EMPTY_COUNTS);
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) {
          setNegotiations([]);
          setError(
            getResponseMessage(err) ?? 'Could not load pricing negotiations.',
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => controller.abort();
  }, [status, merchantId, page, refreshToken]);

  const refresh = useCallback(() => setRefreshToken((token) => token + 1), []);

  return {
    negotiations,
    total,
    counts,
    page,
    setPage,
    isLoading,
    error,
    refresh,
  };
};
